<template>
  <div class="importar-view">
    <h2 class="titulo">Importar exámenes desde PDF</h2>
    <p class="subtitulo">El PDF será procesado por IA para extraer los valores automáticamente.</p>

    <!-- Step 1: Upload -->
    <div v-if="step === 'upload'" class="upload-card">
      <label class="drop-zone" :class="{ 'drag-over': dragging }"
        @dragover.prevent="dragging = true"
        @dragleave="dragging = false"
        @drop.prevent="onDrop">
        <input type="file" accept="application/pdf" @change="onFileChange" hidden ref="fileInput" />
        <div class="drop-icon">📄</div>
        <div class="drop-text">
          <span v-if="!selectedFile">Arrastra un PDF aquí o <u>haz click para seleccionar</u></span>
          <span v-else class="file-name">{{ selectedFile.name }}</span>
        </div>
      </label>
      <div v-if="error" class="error-message">{{ error }}</div>
      <button class="btn btn-primary" :disabled="!selectedFile || loading" @click="procesarPDF">
        <span v-if="loading">⏳ Procesando…</span>
        <span v-else>Extraer datos</span>
      </button>
    </div>

    <!-- Step 2: Preview & edit -->
    <div v-if="step === 'preview'">
      <div class="preview-header">
        <div class="meta-row">
          <label>RUT</label>
          <input v-model="rut" class="meta-input" placeholder="16432481-8" />
          <label>Fecha</label>
          <input v-model="fecha" type="date" class="meta-input" />
        </div>
        <p class="hint">Revisa y corrige los datos antes de importar. Puedes editar cualquier celda.</p>
      </div>

      <div class="match-legend">
        <span class="dot dot-matched"></span> Nombre reconocido
        <span class="dot dot-new"></span> Nombre nuevo
      </div>

      <table class="preview-tabla">
        <thead>
          <tr>
            <th>Examen</th>
            <th>Resultado</th>
            <th>Unidad</th>
            <th>Ref. inf.</th>
            <th>Ref. sup.</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(ex, i) in examenes" :key="i">
            <td class="td-examen">
              <span class="dot" :class="ex.matched ? 'dot-matched' : 'dot-new'" :title="ex.matched ? 'Nombre reconocido en BD' : 'Nombre nuevo — no existe en BD'"></span>
              <input v-model="ex.examen" class="cell-input wide" @input="ex.matched = false" />
            </td>
            <td><input v-model.number="ex.resultado" type="number" step="any" class="cell-input narrow" /></td>
            <td><input v-model="ex.unidad" class="cell-input narrow" /></td>
            <td><input v-model.number="ex.intervalo_inferior" type="number" step="any" class="cell-input narrow" /></td>
            <td><input v-model.number="ex.intervalo_superior" type="number" step="any" class="cell-input narrow" /></td>
            <td><button class="btn-remove" @click="examenes.splice(i, 1)" title="Eliminar">✕</button></td>
          </tr>
        </tbody>
      </table>

      <div class="preview-actions">
        <button class="btn btn-secondary" @click="reset">← Volver</button>
        <span class="row-count">{{ examenes.length }} exámenes</span>
        <button class="btn btn-primary" :disabled="!canImport || importing" @click="confirmarImport">
          <span v-if="importing">⏳ Importando…</span>
          <span v-else>Importar {{ examenes.length }} registros</span>
        </button>
      </div>
      <div v-if="error" class="error-message">{{ error }}</div>
    </div>

    <!-- Step 3: Done -->
    <div v-if="step === 'done'" class="done-card">
      <div class="done-icon">✅</div>
      <h3>Importación completa</h3>
      <p>Se insertaron <strong>{{ insertedCount }}</strong> registros para RUT {{ rut }}.</p>
      <button class="btn btn-primary" @click="reset">Importar otro PDF</button>
    </div>
  </div>
</template>

<script>
import sangreApi from '@/api/sangre'

export default {
  name: 'ImportarPDF',
  data() {
    return {
      step: 'upload',
      selectedFile: null,
      dragging: false,
      loading: false,
      importing: false,
      error: null,
      rut: '',
      fecha: '',
      examenes: [],
      insertedCount: 0,
    }
  },
  computed: {
    canImport() {
      return this.rut.trim() && this.fecha && this.examenes.length > 0
    }
  },
  methods: {
    onFileChange(e) {
      this.selectedFile = e.target.files[0] || null
      this.error = null
    },
    onDrop(e) {
      this.dragging = false
      const f = e.dataTransfer.files[0]
      if (f && f.type === 'application/pdf') {
        this.selectedFile = f
        this.error = null
      } else {
        this.error = 'Solo se aceptan archivos PDF.'
      }
    },
    async procesarPDF() {
      this.loading = true
      this.error = null
      try {
        const { data } = await sangreApi.uploadPDF(this.selectedFile)
        this.rut = data.rut || ''
        this.fecha = data.fecha || ''
        this.examenes = data.examenes.map(ex => ({ ...ex }))
        this.step = 'preview'
      } catch (e) {
        this.error = e.response?.data?.detail || 'Error al procesar el PDF.'
      } finally {
        this.loading = false
      }
    },
    async confirmarImport() {
      if (!this.canImport) return
      this.importing = true
      this.error = null
      try {
        const payload = {
          rut: this.rut.trim(),
          examenes: this.examenes.map(ex => ({
            examen: ex.examen,
            fecha: `${this.fecha}T00:00:00`,
            resultado: Number(ex.resultado),
            unidad: ex.unidad || null,
            intervalo_inferior: ex.intervalo_inferior != null && ex.intervalo_inferior !== '' ? Number(ex.intervalo_inferior) : null,
            intervalo_superior: ex.intervalo_superior != null && ex.intervalo_superior !== '' ? Number(ex.intervalo_superior) : null,
            descripcion: ex.descripcion || null,
          }))
        }
        const { data } = await sangreApi.importar(payload)
        this.insertedCount = data.inserted
        this.step = 'done'
      } catch (e) {
        this.error = e.response?.data?.detail || 'Error al importar.'
      } finally {
        this.importing = false
      }
    },
    reset() {
      this.step = 'upload'
      this.selectedFile = null
      this.error = null
      this.rut = ''
      this.fecha = ''
      this.examenes = []
      this.insertedCount = 0
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    }
  }
}
</script>

<style scoped>
.importar-view { max-width: 900px; }
.titulo { font-size: 1.6rem; font-weight: 700; color: #2d3748; margin-bottom: 0.2em; }
.subtitulo { color: #718096; font-size: 0.9rem; margin-bottom: 1.8em; }

/* Upload card */
.upload-card { display: flex; flex-direction: column; gap: 1.2em; max-width: 480px; }
.drop-zone {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  border: 2px dashed #cbd5e0; border-radius: 12px; padding: 2.5em 2em;
  cursor: pointer; transition: border-color 0.2s, background 0.2s; background: #f7fafc;
}
.drop-zone.drag-over { border-color: #4299e1; background: #ebf8ff; }
.drop-icon { font-size: 2.5rem; margin-bottom: 0.5em; }
.drop-text { color: #718096; font-size: 0.95rem; text-align: center; }
.file-name { color: #2d3748; font-weight: 600; }

/* Preview */
.preview-header { margin-bottom: 1.2em; }
.meta-row { display: flex; align-items: center; gap: 0.8em; margin-bottom: 0.6em; flex-wrap: wrap; }
.meta-row label { font-weight: 600; color: #4a5568; font-size: 0.9rem; }
.meta-input {
  border: 1px solid #cbd5e0; border-radius: 6px; padding: 0.35em 0.7em;
  font-size: 0.9rem; color: #2d3748; outline: none;
}
.meta-input:focus { border-color: #4299e1; }
.hint { color: #718096; font-size: 0.82rem; }

.match-legend {
  display: flex; align-items: center; gap: 1.2em; margin-bottom: 0.7em;
  font-size: 0.82rem; color: #718096;
}
.dot {
  display: inline-block; width: 10px; height: 10px;
  border-radius: 50%; margin-right: 4px; vertical-align: middle; flex-shrink: 0;
}
.dot-matched { background: #48bb78; }
.dot-new     { background: #ed8936; }
.td-examen   { display: flex; align-items: center; gap: 4px; }

.preview-tabla { width: 100%; border-collapse: collapse; font-size: 0.88rem; margin-bottom: 1.2em; }
.preview-tabla th {
  background: #2d3748; color: #fff; padding: 0.6em 0.8em;
  text-align: left; font-weight: 600;
}
.preview-tabla td { padding: 0.3em 0.4em; border-bottom: 1px solid #e2e8f0; }
.cell-input {
  border: 1px solid transparent; border-radius: 4px; padding: 0.3em 0.5em;
  font-size: 0.88rem; width: 100%; background: transparent;
}
.cell-input:focus { border-color: #4299e1; background: #fff; outline: none; }
.cell-input.wide { min-width: 180px; }
.cell-input.narrow { max-width: 90px; }
.btn-remove {
  background: none; border: none; color: #fc8181; cursor: pointer;
  font-size: 1rem; padding: 0.2em 0.5em; border-radius: 4px;
}
.btn-remove:hover { background: #fff5f5; }

.preview-actions {
  display: flex; align-items: center; gap: 1em; flex-wrap: wrap;
}
.row-count { color: #718096; font-size: 0.88rem; flex: 1; }

/* Done */
.done-card {
  text-align: center; padding: 3em 2em; background: #f0fff4;
  border-radius: 12px; max-width: 400px;
}
.done-icon { font-size: 3rem; margin-bottom: 0.5em; }
.done-card h3 { font-size: 1.4rem; color: #276749; margin-bottom: 0.4em; }
.done-card p { color: #4a5568; margin-bottom: 1.5em; }
</style>
